import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;

    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formUrl = 'https://readdy.ai/api/form/d9pd0josenbniolest4g';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 韩漫阅读',
      description: '韩漫阅读专注耽美题材无删减韩国漫画，提供下拉式免费在线阅读模式。',
      url: 'https://hanman.life/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫阅读"
        description="了解韩漫阅读的品牌故事与发展历程。专注耽美题材无删减韩国漫画，为读者提供优质下拉式免费在线阅读体验。"
        keywords="韩漫阅读,关于韩漫阅读,耽美漫画平台,韩国漫画网站,免费韩漫平台"
        structuredData={structuredData}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于韩漫阅读
            </h1>
            <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              韩漫阅读成立于2024年，是一个专注于韩国耽美漫画（BL）的在线阅读平台。
              我们致力于为耽美漫画爱好者提供最优质、最完整的阅读体验。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            {[
              {
                icon: 'ri-shield-check-line',
                title: '无删减内容',
                desc: '所有漫画均保留原版完整内容，尊重原作者创作，让您阅读到最纯正的韩国耽美漫画。',
              },
              {
                icon: 'ri-smartphone-line',
                title: '下拉式阅读',
                desc: '专为网页和手机优化的下拉式阅读模式，无需频繁翻页，滑动即可沉浸式畅读。',
              },
              {
                icon: 'ri-price-tag-3-line',
                title: '完全免费',
                desc: '平台上所有漫画均免费提供在线阅读，无需会员、无需付费，真正实现零门槛阅读。',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="p-6 rounded-lg bg-background-100 text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100">
                  <i className={`${item.icon} text-xl text-primary-500`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-lg bg-background-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议？欢迎通过以下方式联系我们，或填写表单。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@hanman.life' },
                { icon: 'ri-map-pin-line', label: '地址', value: '韩国首尔特别市江南区' },
                { icon: 'ri-customer-service-line', label: '客服时间', value: '周一至周五 9:00-18:00' },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className={`${item.icon} text-primary-500`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-background-50 p-6 rounded-lg"
              data-readdy-form=""
            >
              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded bg-accent-100 text-accent-700 text-sm">
                  感谢您的留言！我们会尽快回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-4 p-3 rounded bg-primary-100 text-primary-700 text-sm">
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">留言</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>
              <div className="honeypot-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <button
                type="submit"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                发送留言
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}